import React, { useState } from 'react';
import { Button, Col, Divider, Form, Input, Popover, Row, Select, Switch, Typography } from 'antd';
import { useFetchConnectorsParametersQuery } from '@/graphql/generated/graphql';
import ConnectorTitle from '@/components/GenericPlanBuilderInput/ConnectorTitle';
import { DraggableFormItem } from './index';
import { If } from '@/components';
import { ColorResult, TwitterPicker } from 'react-color';
import { DeleteOutlined, FontColorsOutlined, PlusCircleOutlined } from '@ant-design/icons';

type Props = {
  formItem: DraggableFormItem;
};

const PreferencesForm: React.FC<Props> = ({ formItem }) => {
  const [color, setColor] = useState(formItem.color);

  const { data, loading } = useFetchConnectorsParametersQuery();

  const parameters = data?.connectors.data.map((connector) => {
    const connectorData = connector.connectorData && JSON.parse(connector.connectorData);
    return {
      value: connector.id,
      label: <ConnectorTitle title={connector.name} connectorData={connectorData} archived={connector.archived} />,
      title: connector.name,
      options: connector.parameters.map((parameter) => ({
        value: parameter.key,
        title: parameter.key,
        label: <Typography.Text type={connector.archived ? 'danger' : undefined}>{parameter.key}</Typography.Text>,
        disabled: connector.archived,
      })),
    };
  });

  const onSelectColor = (value: ColorResult) => {
    setColor(value.hex);
  };

  return (
    <>
      <Form.Item name={['preferences', formItem.id, 'id']} hidden noStyle initialValue={formItem.id}>
        <Input />
      </Form.Item>
      <Form.Item name={['preferences', formItem.id, 'name']} label="Field Name" rules={[{ required: true }]}>
        <Select allowClear filterOption options={parameters} showSearch loading={loading} />
      </Form.Item>
      <Form.Item label="Display Label" name={['preferences', formItem.id, 'label']} rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label="Placeholder" name={['preferences', formItem.id, 'placeholder']}>
        <Input />
      </Form.Item>
      <Form.Item label="Required" name={['preferences', formItem.id, 'required']} valuePropName="checked">
        <Switch />
      </Form.Item>
      <If condition={!!formItem.withOptions}>
        <>
          <Divider />
          <Form.List name="userAttributes">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => {
                  return (
                    <Row key={field.key} gutter={16} justify="space-between">
                      <Col span={11}>
                        <Form.Item name={['options', field.key, 'label']}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item name={['options', field.key, 'value']}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <If condition={fields.length > 1}>
                        <Col span={1}>
                          <Button onClick={() => remove(field.name)} shape="circle" icon={<DeleteOutlined />} />
                        </Col>
                      </If>
                    </Row>
                  );
                })}
                <Col span={1}>
                  <Button onClick={() => add()} shape="circle" icon={<PlusCircleOutlined />} type="primary" />
                </Col>
              </>
            )}
          </Form.List>
        </>
      </If>
      <Divider />
      <Form.Item name={['preferences', formItem.id, 'color']} label="Color">
        <Popover trigger="click" placement="bottom" content={<TwitterPicker onChange={onSelectColor} />}>
          <Button icon={<FontColorsOutlined style={{ color: color }} />} />
        </Popover>
      </Form.Item>
      <Form.Item label="Classname" name={['preferences', formItem.id, 'classname']}>
        <Input />
      </Form.Item>
      <Divider />
    </>
  );
};

export default PreferencesForm;
